import React, { useRef, useState } from "react";
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import Button from "components/UI/Button";
import { Background, Main } from "styles/themeColors";
import Tabs from "components/UI/Tabs";
import { GENERAL_CONSTANTS } from "constants/general";
import MeasurementsTable from "../Measurements/MeasurementsTable";
import WarningBanner from "components/UI/WarningBanner";
import { MESAUREMENTS_UNSAVED_CHANGES } from "constants/measurements";
import EmailConfigurationSettings from "common/EmailConfigurationSettings";
import Vendor from "data/Vendor";
import Page from "components/UI/Page";
import { BusinessUnit } from "data/BusinessUnit";
import FeatureFlag from "data/FeatureFlag";
import UserRoles from "components/UserRoles/UserRoles";
import AutomationSettingsWrapper from "./AutomationSettingsWrapper";
import { useAuth } from "lib/Auth";
import { isOfmpAdmin } from "data/User";
import { getSettingTabs } from "utils/settings";
import { useReadPermission } from "hooks/useUserRoles";
import { PermissionCode } from "data/UserPermissions";
import { ReadOnlyWrapper } from "components/common/ReadOnlyWrapper";
import EmailSettings from "./EmailSettings";
import ProductBadgesTab from "./ProductBadges/ProductBadgesTab";

interface Props {
    selectedVendor: Vendor;
    selectedBusinessUnit?: BusinessUnit;
    featureFlags?: FeatureFlag;
}

const Settings: React.FC<Props> = ({ selectedVendor, selectedBusinessUnit, featureFlags }: Props) => {
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const { user } = useAuth();
    const isAdmin = isOfmpAdmin(user!);

    const measurementsTableRef = useRef<{ handleSave: () => void } | null>(null);
    const emailNotificationsRef = useRef<{ handleSave: () => void } | null>(null);
    const userPermissionsTabRef = useRef<{ handleSave: () => void } | null>(null);
    const emailSettingsRef = useRef<{ handleSave: () => void } | null>(null);
    const automationSettingsTabRef = useRef<{ handleSave: () => void } | null>(null);
    const productBadgesTabRef = useRef<{ handleSave: () => void } | null>(null);

    const { readOnly: emailRemindersReadyOnly } = useReadPermission(PermissionCode.EMAIL_REMINDERS);
    const { readOnly: paymentRemindersReadyOnly } = useReadPermission(PermissionCode.PAYMENT_REMINDERS);
    const { readOnly: automationSettingsReadOnly } = useReadPermission(PermissionCode.AUTOMATION_SETTINGS);

    const tabRefs = [
        emailNotificationsRef,
        measurementsTableRef,
        ...(isAdmin ? [userPermissionsTabRef, emailSettingsRef] : []),
        automationSettingsTabRef,
        ...(isAdmin ? [productBadgesTabRef] : [])
    ];

    const tabPermissionMap: { [index: number]: boolean } = {
        0: emailRemindersReadyOnly && paymentRemindersReadyOnly,
        1: !isAdmin,
    };

    // Conditionally add index 2 if user is admin
    if (isAdmin) {
        tabPermissionMap[2] = !isAdmin; // or any other condition specific to userPermissionsTab
        tabPermissionMap[3] = !isAdmin; // or any other condition specific to emailSettingsTab
        tabPermissionMap[4] = automationSettingsReadOnly;
        tabPermissionMap[5] = !isAdmin; // or any other condition specific to productBadgesTab
    } else {
        tabPermissionMap[2] = automationSettingsReadOnly;
    }

    const onSave = () => {
        tabRefs[activeTab]?.current?.handleSave?.();
    };

    const handleTabChange = (tabIndex: number | undefined) => {
        if (tabIndex !== undefined) {
            setActiveTab(tabIndex);
            setIsSaveDisabled(false);
            setHasUnsavedChanges(false);
        }
    };

    const tabComponents = [
        <Page key="email-settings" padding="0 24px 0 24px">
            <EmailConfigurationSettings
                ref={emailNotificationsRef}
                setIsSaveDisabled={setIsSaveDisabled}
                setHasUnsavedChanges={setHasUnsavedChanges}
                selectedVendor={selectedVendor}
                selectedBusinessUnit={selectedBusinessUnit}
                featureFlags={featureFlags}
                emailRemindersReadyOnly={emailRemindersReadyOnly}
                paymentRemindersReadyOnly={paymentRemindersReadyOnly}
            />
        </Page>,
        <MeasurementsTable
            key="measurements"
            ref={measurementsTableRef}
            setIsSaveDisabled={setIsSaveDisabled}
            setHasUnsavedChanges={setHasUnsavedChanges}
            readonly={!isAdmin}
        />,
        ...(isAdmin
            ? [
                <Page key="user-roles" padding="0 24px 0 24px">
                    <UserRoles
                        ref={userPermissionsTabRef}
                        setIsSaveDisabled={setIsSaveDisabled}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                        selectedVendor={selectedVendor}
                    />
                </Page>,
                <Page key="email-settings" padding="0 24px 0 24px">
                    <EmailSettings
                        ref={emailSettingsRef}
                        setIsSaveDisabled={setIsSaveDisabled}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                        selectedVendor={selectedVendor}
                    />
                </Page>
            ]
            : []),
        <Page key="automation-settings" padding="0 24px 0 24px">
            <AutomationSettingsWrapper
                ref={automationSettingsTabRef}
                featureFlags={featureFlags!}
                selectedVendor={selectedVendor}
                selectedBusinessUnit={selectedBusinessUnit}
                automationSettingsReadOnly={automationSettingsReadOnly}
            />
        </Page>,
        // Product Badges must stay LAST — tab labels, refs and tabPermissionMap are index-aligned.
        <Page key="product-badges" padding="0 24px 0 24px">
            <ProductBadgesTab
                ref={productBadgesTabRef}
                setIsSaveDisabled={setIsSaveDisabled}
                setHasUnsavedChanges={setHasUnsavedChanges}
            />
        </Page>
    ];

    return (
        <Page bgColor={Background.white} width="100%" padding="0" margin="0">
            <Tabs
                theme="brandDark"
                margin="8px 24px 8px 0"
                height="36px !important"
                borderRadius="20px"
                tabPanelPadding="0 !important"
                tabs={getSettingTabs(isAdmin, featureFlags!)}
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                padding="15px 24px"
                className="settings-tabs"
                actionButtons={
                    <FlexboxContainerComponent width="100%" border="none" alignItems="center">
                        <div style={{ border: "none", display: "flex", justifyContent: "flex-end", width: "100%" }}>
                            {hasUnsavedChanges && !isSaveDisabled && (
                                <WarningBanner
                                    message={MESAUREMENTS_UNSAVED_CHANGES}
                                    fontSize="14px"
                                    margin="0px 10px 0px 0px"
                                />
                            )}
                            <ReadOnlyWrapper readOnly={tabPermissionMap[activeTab]}>
                                <Button
                                    borderColor={Main.brandDark}
                                    background={Main.brandDark}
                                    key="save"
                                    onClick={onSave}
                                    disabled={isSaveDisabled || tabPermissionMap[activeTab]}
                                >
                                    {GENERAL_CONSTANTS.SAVE}
                                </Button>
                            </ReadOnlyWrapper>
                        </div>
                    </FlexboxContainerComponent>
                }
            >
                {tabComponents.map((tabContent) => tabContent)}
            </Tabs>
        </Page>
    );
};

export default Settings;
